import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { COMPANY, IMAGES, SERVICE_OPTIONS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    origin: "",
    destination: "",
    volume: "",
    details: "",
    message: "",
};

const inputClass =
    "w-full border border-gray-300 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-gray-400 transition-colors focus:border-gold focus:outline-none";

function Field({ label, id, testid, required, error, children }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.18em] text-ink"
            >
                {label} {required && <span className="text-gold" aria-hidden="true">*</span>}
            </label>
            {children}
            {error && (
                <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-600" data-testid={`${testid}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}

export default function Contact() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const update = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((err) => ({ ...err, [key]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Please enter your name.";
        if (!form.email.trim()) next.email = "Please enter your email.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
            next.email = "Please enter a valid email address.";
        if (!form.service) next.service = "Please select a service.";
        return next;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const next = validate();
        setErrors(next);
        if (Object.keys(next).length > 0) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/quotes`, {
                name: form.name.trim(),
                company: form.company.trim() || null,
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                service: form.service,
                origin: form.origin.trim() || null,
                destination: form.destination.trim() || null,
                volume: form.volume.trim() || null,
                details: form.details.trim() || null,
                message: form.message.trim() || null,
            });
            toast.success("Quote request sent.", {
                description: "Our dispatch team will reach out shortly.",
            });
            setForm(INITIAL_FORM);
        } catch (err) {
            toast.error("Something went wrong.", {
                description: "Please try again, or call us at (310) 363-0713.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Request a Quote | Contact Pacific Expressway"
                description="Tell us what you need to move. Request a quote for port drayage, transloading, warehousing, overweight freight or dedicated transportation in Southern California. Call (310) 363-0713."
                path="/contact"
            />
            <section
                className="relative flex min-h-[52svh] items-end overflow-hidden bg-ink"
                data-testid="contact-hero"
            >
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={IMAGES.containers} alt="" className="h-full w-full object-cover opacity-45" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" aria-hidden="true" />
                <div className="container-px relative w-full pb-14 pt-40 lg:pb-20">
                    <Eyebrow>Let&rsquo;s Talk Logistics</Eyebrow>
                    <h1 className="mt-6 max-w-3xl font-heading text-3xl sm:text-4xl lg:text-[56px] font-bold tracking-tight leading-[1.15] sm:leading-[1.1] lg:leading-[1.06] text-white">
                        <MaskedLine delay={0.2}>
                            Tell Us What You Need to Move<span className="text-gold">.</span>
                        </MaskedLine>
                    </h1>
                </div>
            </section>

            <section className="bg-mist py-16 sm:py-20 lg:py-24" data-testid="quote-form-section">
                <div className="container-px grid gap-10 lg:grid-cols-5 lg:gap-12">
                    <Reveal className="lg:col-span-3">
                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            data-testid="quote-form"
                            className="bg-white p-6 sm:p-10 border border-gray-200"
                        >
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-ink">
                                Request a Quote
                            </h2>
                            <p className="mt-3 text-[15px] text-gray-600">
                                Fill out what you know — our team will handle the rest.
                            </p>
                            <div className="mt-8 grid gap-6 sm:grid-cols-2">
                                <Field label="Name" id="name" testid="contact-form-name" required error={errors.name}>
                                    <input
                                        id="name"
                                        data-testid="contact-form-name"
                                        type="text"
                                        autoComplete="name"
                                        value={form.name}
                                        onChange={update("name")}
                                        aria-invalid={!!errors.name}
                                        aria-describedby={errors.name ? "name-error" : undefined}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Company" id="company" testid="contact-form-company">
                                    <input
                                        id="company"
                                        data-testid="contact-form-company"
                                        type="text"
                                        autoComplete="organization"
                                        value={form.company}
                                        onChange={update("company")}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Email" id="email" testid="contact-form-email" required error={errors.email}>
                                    <input
                                        id="email"
                                        data-testid="contact-form-email"
                                        type="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        aria-invalid={!!errors.email}
                                        aria-describedby={errors.email ? "email-error" : undefined}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Phone" id="phone" testid="contact-form-phone">
                                    <input
                                        id="phone"
                                        data-testid="contact-form-phone"
                                        type="tel"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Service Needed" id="service" testid="contact-form-service" required error={errors.service}>
                                    <select
                                        id="service"
                                        data-testid="contact-form-service"
                                        value={form.service}
                                        onChange={update("service")}
                                        aria-invalid={!!errors.service}
                                        aria-describedby={errors.service ? "service-error" : undefined}
                                        className={inputClass}
                                    >
                                        <option value="">Select a service…</option>
                                        {SERVICE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Estimated Volume" id="volume" testid="contact-form-volume">
                                    <input
                                        id="volume"
                                        data-testid="contact-form-volume"
                                        type="text"
                                        placeholder="e.g. 20 containers / month"
                                        value={form.volume}
                                        onChange={update("volume")}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Origin" id="origin" testid="contact-form-origin">
                                    <input
                                        id="origin"
                                        data-testid="contact-form-origin"
                                        type="text"
                                        placeholder="e.g. Port of Long Beach"
                                        value={form.origin}
                                        onChange={update("origin")}
                                        className={inputClass}
                                    />
                                </Field>
                                <Field label="Destination" id="destination" testid="contact-form-destination">
                                    <input
                                        id="destination"
                                        data-testid="contact-form-destination"
                                        type="text"
                                        placeholder="e.g. Rancho Dominguez, CA"
                                        value={form.destination}
                                        onChange={update("destination")}
                                        className={inputClass}
                                    />
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Freight / Container Details" id="details" testid="contact-form-details">
                                        <textarea
                                            id="details"
                                            data-testid="contact-form-details"
                                            rows={3}
                                            placeholder="Container counts, weights, commodity, special requirements…"
                                            value={form.details}
                                            onChange={update("details")}
                                            className={`${inputClass} resize-y`}
                                        />
                                    </Field>
                                </div>
                                <div className="sm:col-span-2">
                                    <Field label="Message" id="message" testid="contact-form-message">
                                        <textarea
                                            id="message"
                                            data-testid="contact-form-message"
                                            rows={4}
                                            value={form.message}
                                            onChange={update("message")}
                                            className={`${inputClass} resize-y`}
                                        />
                                    </Field>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-form-submit-button"
                                className="group mt-8 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {submitting ? "Sending…" : "Submit Quote Request"}
                                {!submitting && (
                                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                                )}
                            </button>
                        </form>
                    </Reveal>

                    <Reveal delay={0.12} className="lg:col-span-2">
                        <aside className="bg-ink p-6 sm:p-10 text-white grain relative" data-testid="contact-info-card">
                            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">
                                Pacific Expressway
                            </h2>
                            <p className="mt-2 text-sm text-gray-400">
                                Family-owned and operating since 2002.
                            </p>
                            <ul className="mt-8 space-y-6 text-[15px]">
                                <li className="flex gap-3">
                                    <MapPin size={18} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                    <span data-testid="contact-address" className="text-gray-300">
                                        {COMPANY.address.street}
                                        <br />
                                        {COMPANY.address.city}
                                    </span>
                                </li>
                                <li>
                                    <a
                                        href={COMPANY.phoneHref}
                                        data-testid="contact-phone"
                                        className="flex gap-3 text-gray-300 transition-colors hover:text-gold"
                                    >
                                        <Phone size={18} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                        {COMPANY.phone}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href={COMPANY.emailHref}
                                        data-testid="contact-email"
                                        className="flex gap-3 break-all text-gray-300 transition-colors hover:text-gold"
                                    >
                                        <Mail size={18} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                        {COMPANY.email}
                                    </a>
                                </li>
                                <li className="flex gap-3">
                                    <Clock size={18} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                    <span data-testid="contact-hours" className="text-gray-300">
                                        {COMPANY.hours.map((h) => (
                                            <span key={h.days} className="block">
                                                <span className="text-gray-500">{h.days}</span>
                                                <br />
                                                {h.time}
                                            </span>
                                        ))}
                                    </span>
                                </li>
                            </ul>
                            <div className="mt-8 border-t border-white/10 pt-6">
                                <p className="text-sm leading-relaxed text-gray-400">
                                    We keep your containers moving — even when the port
                                    doesn&rsquo;t.
                                </p>
                            </div>
                        </aside>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white pb-16 sm:pb-20 lg:pb-24" data-testid="map-section">
                <div className="container-px">
                    <Reveal>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-ink">
                            Find Our Yard
                        </h2>
                        <p className="mt-3 text-[15px] text-gray-600">
                            Minutes from the Ports of Los Angeles and Long Beach.
                        </p>
                        <div className="mt-8 overflow-hidden border border-gray-200">
                            <iframe
                                title="Map — Pacific Expressway, 18726 S. Laurel Park Rd., Rancho Dominguez, CA 90220"
                                src={COMPANY.mapEmbed}
                                loading="lazy"
                                data-testid="contact-map"
                                className="h-[320px] w-full sm:h-[420px] [filter:grayscale(0.35)_contrast(1.05)]"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
